import config from "@/config";

/* Libraries */
import * as _ from "lodash";
import moment from "moment-timezone";
import WorkerTimer from "@/plugins/WorkerTimer";

/* Enums */
import ConferenceRoles from "@/enums/ConferenceRoles";
import StartLiveStatus from "@/enums/StartLiveStatus";
import SyndicationTypes from "@/enums/SyndicationTypes";
import TransitionType from "@/enums/TransitionType";

const sequencerPlayerStudioStatusMixin = {
	data: () => ({
		iotDisconnectTimeout: null,
		transitionInterval: null,
		transitionTime: 0,
		sequencerConnected: false
	}),

	computed: {
		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		sequencer() {
			return this.$store.getters.getSequencer(this.selectedStation?.StationID);
		},

		startLiveSequencerSnapshot() {
			return this.sequencer?.StartLiveSequencerSnapshot;
		},

		sequencerStartLiveState() {
			return this.sequencer?.StartLiveSequencerSnapshot?.StartLiveState;
		},

		sequencerIsLive() {
			return [
				StartLiveStatus.WaitingForTransition,
				StartLiveStatus.Connected,
				StartLiveStatus.WaitingForEndOfSyndication
			].includes(this.startLiveSequencerSnapshot?.StartLiveState);
		},

		sequencerWaitingForTransition() {
			return this.sequencerStartLiveState === StartLiveStatus.WaitingForTransition;
		},

		sequencerWaitingForEndOfShow() {
			return this.sequencerStartLiveState === StartLiveStatus.WaitingForEndOfShow;
		},

		sequencerWaitingForEndOfSyndication() {
			return this.sequencerStartLiveState === StartLiveStatus.WaitingForEndOfSyndication;
		},

		sequencerConnectedCurrently() {
			return [StartLiveStatus.WaitingForTransition, StartLiveStatus.Connected].includes(
				this.sequencerStartLiveState
			) && this.transitionTime <= 0;
		},

		livePlayers() {
			return this.$store.getters.getLivePlayers();
		},

		playersSnapshotsFetched() {
			return this.livePlayers.every(p => !!p.listenUrl);
		},

		player() {
			return this.$store.getters.getLivePlayerSnapshot();
		},

		playerIsLive() {
			return !!this.player?.liveMode;
		},

		isSyndicationOriginated() {
			return this.selectedStation?.SyndicationType === SyndicationTypes.SyndicationOrigination;
		},

		isSplitMasterGoLiveDisabled() {
			const station = this.selectedStation ||
							this.$store.getters.liveMode() && this.$store.getters.liveModeSelectedStation();

			return station?.SyndicationType === SyndicationTypes.SplitMaster && config.ORIGIN === "ZETTACLOUD";
		},

		stationConnectedToStudio() {
			return this.$store.getters.liveMode() &&
				   (this.sequencerIsLive || this.isSyndicationOriginated || this.isSplitMasterGoLiveDisabled) &&
				   this.connectedHost &&
				   this.hostStudio === this.studioSettings?.reference &&
				   this.hostPeer.clientID === sessionStorage.getItem("clientID");
		},

		conferenceSnapshots() {
			return this.$store.getters.getLiveConferenceSnapshots();
		},

		SIPPeers() {
			return this.$store.getters.getSIPPeers();
		},

		webRTCPeers() {
			return this.$store.getters.getWebRTCPeers();
		},

		allPeers() {
			return _.uniqWith(this.SIPPeers.concat(this.webRTCPeers), _.isEqual);
		},

		hostPeer() {
			return this.allPeers.find(peer => parseInt(peer.role) === ConferenceRoles.Host);
		},

		participantPeers() {
			return this.allPeers.filter(p => p.role === ConferenceRoles.Participant);
		},

		guestPeers() {
			return this.allPeers.filter(p => p.role === ConferenceRoles.Guest);
		},

		nonGuestParticipantPeers() {
			return this.participantPeers.filter(p => !p.user.startsWith("guestp"));
		},

		connectedHost() {
			return this.hostPeer && ["CALLING", "CONNECTED"].includes(this.hostPeer.status);
		},

		userPeer() {
			let userPeer = this.SIPPeers.find(p => p.studio === this.studioSettings?.reference);
			if (!userPeer) {
				userPeer = this.webRTCPeers.find(p =>
					p.studio === this.studioSettings?.reference && p.user === this.$store.getters.getUserID() &&
					p.clientID === sessionStorage.getItem("clientID")
				);
			}
			return userPeer;
		},

		userIsHost() {
			return this.userPeer?.role === ConferenceRoles.Host;
		},

		userIsParticipant() {
			return this.userPeer?.role === ConferenceRoles.Participant;
		},

		userIsGuest() {
			return this.userPeer?.role === ConferenceRoles.Guest;
		},

		hostStudio() {
			return this.hostPeer?.studio;
		},

		userAnyRole() {
			return !!this.userPeer;
		},

		userRole() {
			return this.userPeer?.role ?? null;
		},

		userConnectionError() {
			if (this.userPeer?.status !== "CONNECTED") {
				return this.userPeer?.status;
			}
			return null;
		},

		studioDefaultLiveOptionIsChooseEveryTime() {
			return this.studioSettings?.preferences?.default_live_option === TransitionType.ChooseEveryTime.value;
		}
	},

	methods: {
		startLiveTransitionInterval() {
			const start = moment(this.startLiveSequencerSnapshot.MicStartDateTime);
			this.transitionTime = start.diff(moment(), "s");
			if (this.transitionTime > 0) {
				this.transitionInterval = WorkerTimer.setInterval(() => {
					if (this.transitionTime > 0) {
						this.transitionTime--;
					}
					else {
						this.clearLiveTransitionInterval();
					}
				}, 1000);
			}
			else {
				this.transitionTime = 0;
			}
		},

		clearLiveTransitionInterval() {
			if (this.transitionInterval) {
				WorkerTimer.clearInterval(this.transitionInterval);
				this.transitionTime = 0;
				this.transitionInterval = null;
			}
		}
	},

	watch: {
		//TODO: move this to Timebox because it's used by all the components that utilize this mixin.
		// We need to use the store for the transition time value
		startLiveSequencerSnapshot(snap, previousSnap) {
			const state = snap?.StartLiveState;
			const previousState = previousSnap?.StartLiveState;
			if ([StartLiveStatus.WaitingForTransition, StartLiveStatus.WaitingForEndOfSyndication].includes(state) &&
				(state !== previousState || snap?.MicStartDateTime !== previousSnap?.MicStartDateTime)) {
				this.clearLiveTransitionInterval();
				this.startLiveTransitionInterval();
			}
			else if (state === StartLiveStatus.WaitingForEndOfShow &&
					 snap?.MicStartDateTime !== previousSnap?.MicStartDateTime &&
					 moment().isSameOrBefore(snap.MicStartDateTime)) {
				this.clearLiveTransitionInterval();
				this.startLiveTransitionInterval();
			}
			if ((!state && previousState) ||
				(state !== previousState && state === StartLiveStatus.WaitingForEndOfShow)) {
				this.clearLiveTransitionInterval()
			}
		},

		sequencerConnectedCurrently(connected, previouslyConnected) {
			if (!connected && previouslyConnected) {
				this.iotDisconnectTimeout = this.$worker.setTimeout(() => {
					this.sequencerConnected = connected;
				}, 5000);
			}
			else if (connected) {
				this.$worker.clearTimeout(this.iotDisconnectTimeout);
				this.iotDisconnectTimeout = null;
				this.sequencerConnected = connected;
			}
		}
	}
}

export default sequencerPlayerStudioStatusMixin;